/**
 * `HookumSocket` — the single client→server transport.
 *
 *   - One WebSocket per session. Reconnects with capped exponential backoff.
 *   - On every (re)connect, automatically sends the `join` envelope (with the
 *     persisted `reclaimToken` if we have one for this room — the secret
 *     authority for seat reclaim per ARCH §9.1).
 *   - Every inbound frame goes through `parseServerMessage` (zod). Malformed
 *     frames are dropped after a console warning — the protocol contract is
 *     law (see protocol/index.ts).
 *   - The socket owns no game logic. It pushes parsed `ServerMessage`s into
 *     the store via `applyServer`, and forwards a `ClientMessage` out on
 *     `send`. That is the entire surface.
 *
 * Anti-cheat is upstream of this file: the server is the one that produces
 * `RedactedView`s; this socket cannot widen what it receives, only validate
 * the shape.
 */
import {
  PROTOCOL_VERSION,
  parseServerMessage,
  type ClientMessage,
  type ServerMessage,
} from "@hookum/protocol";
import { roomSocketUrl } from "../env.js";
import {
  loadReclaimToken,
  saveReclaimToken,
} from "../state/storage.js";
import type { Store } from "../state/store.js";

const HEARTBEAT_MS = 25_000;
const BACKOFF_MIN_MS = 500;
const BACKOFF_MAX_MS = 15_000;
const BACKOFF_FACTOR = 1.7;

/**
 * Server error codes that mean "the join itself failed; reconnecting with the
 * same arguments will fail the same way". Receiving any of these stops the
 * reconnect loop and returns the user to the RoomGate so they can correct the
 * input (typoed code, full room, expired/forged reclaim token). Fix per
 * docs/PHASE3-UX-REVIEW.md Finding #3 — without this the socket reconnects
 * forever on a typoed code, showing "Reconnecting (attempt N)…" with no exit.
 */
const TERMINAL_ERROR_CODES: ReadonlySet<string> = new Set([
  "BAD_CODE",
  "ROOM_FULL",
  "RECLAIM_FAILED",
  "GAME_IN_PROGRESS",
]);

export interface SocketFactory {
  /** Built so tests can swap in an in-memory transport. */
  (url: string): WebSocketLike;
}

/** Just the slice of WebSocket the transport actually touches. */
export interface WebSocketLike {
  readyState: number;
  onopen: ((ev: Event) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

const defaultFactory: SocketFactory = (url) =>
  new WebSocket(url) as unknown as WebSocketLike;

export interface SocketOptions {
  readonly factory?: SocketFactory;
  /** Tests inject a faker; production gets Math.random. */
  readonly jitter?: () => number;
  /** Tests inject a fake clock; production gets setTimeout. */
  readonly setTimer?: (fn: () => void, ms: number) => unknown;
  readonly clearTimer?: (handle: unknown) => void;
}

export class HookumSocket {
  private readonly store: Store;
  private readonly opts: Required<SocketOptions>;

  private ws: WebSocketLike | null = null;
  private code: string | null = null;
  private name = "";

  private attempt = 0;
  private wantOpen = false;
  private reconnectTimer: unknown = null;
  private heartbeatTimer: unknown = null;
  private latestReclaim: string | null = null;

  constructor(store: Store, opts: SocketOptions = {}) {
    this.store = store;
    this.opts = {
      factory: opts.factory ?? defaultFactory,
      jitter: opts.jitter ?? Math.random,
      setTimer:
        opts.setTimer ??
        ((fn, ms) => (globalThis.setTimeout as (f: () => void, n: number) => unknown)(fn, ms)),
      clearTimer:
        opts.clearTimer ??
        ((h) => (globalThis.clearTimeout as (h: unknown) => void)(h)),
    };
  }

  /** Public: open (or re-open) a session bound to `code` as `name`. */
  connect(code: string, name: string): void {
    this.code = code;
    this.name = name;
    this.wantOpen = true;
    this.attempt = 0;
    this.latestReclaim = loadReclaimToken(code);
    this.store.setRoom(code, name);
    this.openSocket();
  }

  /** Public: tear it all down (back-to-gate / unmount). */
  disconnect(): void {
    this.wantOpen = false;
    this.clearTimers();
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      try {
        ws.close(1000, "client-disconnect");
      } catch {
        /* already gone */
      }
    }
    this.store.setConnection({ kind: "idle" });
  }

  /** Public: emit one client message. Drops silently if not open — the store
   * already shows the disconnected state and the next reconnect will resync
   * via the server's `state`/`lobby` push. */
  send(msg: ClientMessage): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== 1) return;
    try {
      ws.send(JSON.stringify(msg));
    } catch {
      /* serializer error is impossible for a typed ClientMessage; defensive */
    }
  }

  // ---- internals -------------------------------------------------------

  private openSocket(): void {
    if (!this.code || !this.wantOpen) return;
    this.store.setConnection(
      this.attempt === 0
        ? { kind: "connecting" }
        : { kind: "reconnecting", attempt: this.attempt },
    );
    const url = roomSocketUrl(this.code);
    let ws: WebSocketLike;
    try {
      ws = this.opts.factory(url);
    } catch (err) {
      this.scheduleReconnect(String((err as Error)?.message ?? err));
      return;
    }
    this.ws = ws;
    ws.onopen = () => this.onOpen();
    ws.onmessage = (ev) => this.onMessage(ev);
    ws.onclose = (ev) => this.onClose(ev);
    ws.onerror = () => {
      /* close will follow */
    };
  }

  private onOpen(): void {
    this.attempt = 0;
    this.store.setConnection({ kind: "open" });
    // Always (re)send `join` on open. If we have a reclaim token for this
    // room the server treats it as the sole authority and rebinds our seat;
    // otherwise we take a fresh seat in the lobby (ARCH §9.1).
    //
    // v1.4.1 (Phase 21.2): also advertise our `protocolVersion`. The server
    // rejects a mismatched version with a `PROTOCOL_MISMATCH` typed terminal
    // error (see onMessage's PROTOCOL_MISMATCH branch). Omitting the field
    // is currently tolerated by the server for one phase to absorb
    // deploy-rollover; we still emit it so the path is testable end-to-end.
    const token = this.latestReclaim ?? undefined;
    this.send(
      token !== undefined
        ? {
            t: "join",
            code: this.code!,
            name: this.name,
            reclaimToken: token,
            protocolVersion: PROTOCOL_VERSION,
          }
        : {
            t: "join",
            code: this.code!,
            name: this.name,
            protocolVersion: PROTOCOL_VERSION,
          },
    );
    this.startHeartbeat();
  }

  private onMessage(ev: MessageEvent): void {
    let raw: unknown;
    try {
      raw =
        typeof ev.data === "string"
          ? JSON.parse(ev.data)
          : JSON.parse(new TextDecoder().decode(ev.data as ArrayBuffer));
    } catch {
      /* malformed frame → ignore; protocol is JSON-only */
      return;
    }
    const parsed = parseServerMessage(raw);
    if (!parsed.ok) {
      // Schema mismatch usually means a protocol-version skew; surface it as a
      // store error so the UI can show a toast.
      this.store.applyServer({
        t: "error",
        code: "BAD_SERVER_MESSAGE",
        message: "received a message that did not match the protocol schema",
      });
      return;
    }
    const m: ServerMessage = parsed.data;
    if (m.t === "youAre" && this.code) {
      this.latestReclaim = m.reclaimToken;
      saveReclaimToken(this.code, m.reclaimToken);
    }
    // PROTOCOL_MISMATCH short-circuit (v1.4.1 Phase 21.2): the server's
    // handshake check rejected our `protocolVersion`. Unlike the other
    // terminal errors below, we want to PRESERVE the room code so
    // DisconnectedPanel renders (with a typed "Refresh now" button), rather
    // than dump the user back to RoomGate where a re-entered code would
    // immediately fail the same way against the same stale client bundle.
    // Reconnecting cannot help — only a full page reload picks up a fresh
    // bundle from Cloudflare Pages.
    if (m.t === "error" && m.code === "PROTOCOL_MISMATCH") {
      this.wantOpen = false;
      this.clearTimers();
      const ws = this.ws;
      this.ws = null;
      if (ws) {
        // Unbind onclose so the imminent close event doesn't overwrite our
        // typed `closed` state with a generic one.
        ws.onclose = null;
        try {
          ws.close(1000, "terminal-error:PROTOCOL_MISMATCH");
        } catch {
          /* already gone */
        }
      }
      this.store.applyServer(m); // also surface as an error toast
      this.store.setConnection({
        kind: "closed",
        reason: m.message,
        errorCode: "PROTOCOL_MISMATCH",
      });
      return;
    }
    // Terminal-error short-circuit: a permanent server-side rejection (bad
    // code, full room, invalid reclaim token, already-started game) must NOT
    // trigger the reconnect loop. We push the error toast onto a freshly-reset
    // store so the App routes back to the RoomGate with the user's last name
    // still prefilled and the toast visible explaining what happened.
    if (m.t === "error" && TERMINAL_ERROR_CODES.has(m.code)) {
      this.wantOpen = false;
      this.clearTimers();
      const ws = this.ws;
      this.ws = null;
      if (ws) {
        try {
          ws.close(1000, "terminal-error:" + m.code);
        } catch {
          /* already gone */
        }
      }
      this.store.reset();
      this.store.applyServer(m); // toast lands on the reset store
      this.store.setConnection({ kind: "idle" });
      return;
    }
    this.store.applyServer(m);
  }

  private onClose(ev: CloseEvent): void {
    this.clearHeartbeat();
    this.ws = null;
    if (!this.wantOpen) {
      this.store.setConnection({ kind: "closed", reason: ev.reason || "closed" });
      return;
    }
    this.scheduleReconnect(ev.reason || `closed-${ev.code}`);
  }

  private scheduleReconnect(reason: string): void {
    this.attempt += 1;
    const base = Math.min(
      BACKOFF_MAX_MS,
      BACKOFF_MIN_MS * Math.pow(BACKOFF_FACTOR, this.attempt - 1),
    );
    const delay = Math.floor(base * (0.7 + 0.6 * this.opts.jitter()));
    this.store.setConnection({ kind: "reconnecting", attempt: this.attempt });
    this.reconnectTimer = this.opts.setTimer(() => {
      this.reconnectTimer = null;
      void reason;
      this.openSocket();
    }, delay);
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatTimer = this.opts.setTimer(() => this.heartbeat(), HEARTBEAT_MS);
  }

  private heartbeat(): void {
    this.send({ t: "heartbeat" });
    this.heartbeatTimer = this.opts.setTimer(() => this.heartbeat(), HEARTBEAT_MS);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      this.opts.clearTimer(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearTimers(): void {
    this.clearHeartbeat();
    if (this.reconnectTimer !== null) {
      this.opts.clearTimer(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
